/* istanbul ignore file */

/**
 * NOTE
 * This is the original "richTextField" that was in core.  We moved it to ui-morpheus because
 * (a) morpheus is currently the only app the uses it and (b) quill has a big cost (~500KB) so
 * we don't want to pay for that download in all apps (just Morpheus)
 *
 * Other approaches e.g. splitting out chunks of core were attempted but not successful so this
 * is the next best thing to do for the time being.
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import { formFieldWrapper } from '@adc/ui-core'
import { isBrowser } from '@adc/core-utils/src/helpers/environments'
import commonPropTypes from '../commonPropTypes'
import stripTemplateTags from './stripTemplateTags'
import styles from './styles.scss'
const cx = classnames.bind(styles)

/**
 * We need to be very careful with this pattern, since we do _not_ want to enable greedby matches here, in case we
 * match between multiple sets of braces at once. We want the shortest possible match possible.
 */
const TEMPLATE_TAGS_MATCH_PATTERN = /({{[^}]+}})/g

let ReactQuill

if (isBrowser) {
  require('./modules/templating')
  ReactQuill = require('react-quill')
} else {
  ReactQuill = () => null
}

const defaultModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    ['link'],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: false,
  },
}

const richTextPropTypes = {
  ...commonPropTypes,
  normalizeOnBlur: PropTypes.func,
  compact: PropTypes.bool,
  formats: PropTypes.array,
  modules: PropTypes.object,
  onFocus: PropTypes.func,
  preserveWhitespace: PropTypes.bool,
  placeholder: PropTypes.string,
}

class RichTextAreaFieldClass extends Component {
  static propTypes = richTextPropTypes

  state = { focused: false }

  // see docs for al formats and modules: https://github.com/zenoamaro/react-quill
  static defaultProps = {
    compact: false,
    formats: [
      'bold',
      'italic',
      'underline',
      'list',
      'bullet',
      'indent',
      'link',
      'variable',
    ],
    modules: defaultModules,
    preserveWhitespace: false,
    defaultValue: null,
  }

  onChange = html => {
    const { onChange, normalizeOnChange } = this.props
    if (normalizeOnChange) {
      html = normalizeOnChange(html)
    }
    onChange(html)
  }

  onBlur = (range, user, editor) => {
    const { onBlur, normalizeOnBlur } = this.props
    let html = editor && editor.getHTML()

    this.setState({ focused: false })

    if (normalizeOnBlur) {
      html = normalizeOnBlur(html)
    }
    onBlur(html)
  }

  onFocus = (...args) => {
    if (this.props.disabled) {
      return
    }
    this.setState({ focused: true })
    this.props.onFocus(...args)
  }

  render() {
    const {
      value,
      defaultValue,
      elmId,
      formats,
      modules,
      compact,
      showError,
      disabled,
      preserveWhitespace,
      placeholder,
    } = this.props

    const conditionalProps = {
      value: value || '',
      defaultValue: defaultValue || '',
    }

    // This signals that we should be an uncontrolled component, which, generally speaking, is required whenever we
    // perform some asymmertic normalization from what is displayed in the ui, to whatever the date should actually be.
    defaultValue !== null
      ? delete conditionalProps.value
      : delete conditionalProps.defaultValue

    return (
      <div data-elm-id={`${elmId}_richtext`}>
        <ReactQuill
          className={cx(
            'editor',
            { compact },
            { focused: this.state.focused },
            { disabled },
            { error: showError }
          )}
          theme='snow'
          onChange={this.onChange}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          formats={formats}
          modules={modules}
          readOnly={disabled}
          preserveWhitespace={preserveWhitespace}
          placeholder={placeholder}
          {...conditionalProps}
        />
      </div>
    )
  }
}

// NOTE: This is extremely coupled with the VariableBlot implementation. See if we can detach them somehow and clean this
// up.
//
const insertTemplateTagsForInitialRender = html =>
  html.replace(
    TEMPLATE_TAGS_MATCH_PATTERN,
    match =>
      `<span class="ql-variable"><span contenteditable="false">${match
        .slice(2)
        .slice(0, -2)}</span></span>`
  )

const RichTextAreaFieldWrapper = formFieldWrapper((fieldProps, showError) => (
  <RichTextAreaFieldClass {...fieldProps} showError={showError} />
))

// This needs to be an uncontrolled component to have the formatting work correctly
const TemplatingRichTextField = ({
  modules,
  templateTags,
  value,
  defaultValue = '',
  ...props
}) => (
  <RichTextAreaFieldWrapper
    {...props}
    defaultValue={insertTemplateTagsForInitialRender(defaultValue)}
    modules={{
      ...defaultModules,
      ...modules,
      templating: {
        suggestions: templateTags,
      },
    }}
    normalizeOnBlur={stripTemplateTags}
    normalizeOnChange={stripTemplateTags}
    componentName='RichTextField'
  />
)

TemplatingRichTextField.propTypes = {
  ...richTextPropTypes,
  templateTags: PropTypes.object,
}

TemplatingRichTextField.TEMPLATE_TAGS_MATCH_PATTERN =
  TEMPLATE_TAGS_MATCH_PATTERN

export default RichTextAreaFieldWrapper
export { TemplatingRichTextField }
